import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ImageContext,
  ImageDocumentStoreItemAdditionalFields,
  ODataDocumentCropSettings,
  RetrievedDocumentReferenceCropSettings,
} from '../../../../components/v2/components/imageHandling/types/types.d';
import { AppDispatch, RootState } from '@store/types';
import { backendCall, createApiCall } from '@reducers/utils';
import { updateImageDocumentInDict } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { setExpirationTimeAsTimestamp } from '@helpers';
import { FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME } from '@components/operatorPanel/constants/operatorPanel.constants';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

// Debounce storage for rotation API calls
const rotateDebounceTimers = new Map<string, NodeJS.Timeout>();

export interface RotateImageDocumentPayload {
  documentId: number;
  onePage: boolean;
}

export const rotateImageDocument = createAsyncThunk<void, RotateImageDocumentPayload, { dispatch: AppDispatch }>(
  'imageHandlingV2/rotateImageDocument',
  async ({ documentId, onePage }, { dispatch, getState }) => {
    const rootState = getState() as RootState;

    setExpirationTimeAsTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);

    // Get the image directly from the dictionary
    const currentImage = rootState.imageHandlingV2.imagesDocumentsDict[documentId];

    if (!currentImage) return;

    const { customRotate, pageNumber, pageCount, id, isPDF, isCropped, cropSettings } = currentImage;

    let angle = getAngleAfterRotateForPage({ customRotate, pageNumber, cropSettings });

    const list: ImageDocumentStoreItemAdditionalFields['customRotate'] = {};

    let updatedImage;
    if (onePage) {
      if (isCropped) {
        const initialAngle = currentImage.cropSettings?.find((s) => s.pageNumber === pageNumber)?.rotationAngle || 0;
        updatedImage = {
          ...currentImage,
          width: currentImage.width,
          height: currentImage.height,
          y: currentImage.x,
          x:
            currentImage.y != null && currentImage.height != null
              ? currentImage.heightOriginal - currentImage.y - currentImage.height
              : currentImage.y,
          customRotate: {
            ...currentImage.customRotate,
            [pageNumber]: convertAngle(angle - initialAngle),
          },
        };
      } else {
        updatedImage = {
          ...currentImage,
          customRotate: {
            ...currentImage.customRotate,
            [pageNumber]: convertAngle(angle),
          },
        };
      }
    } else {
      Array.from({ length: pageCount }, (_, i) => i + 1).forEach((i) => {
        const currentPageAngle = getAngleAfterRotateForPage({ customRotate, pageNumber: i, cropSettings });
        list[i] = convertAngle(currentPageAngle);
      });
      updatedImage = {
        ...currentImage,
        customRotate: list,
      };
    }

    dispatch(
      updateImageDocumentInDict({
        documentId: updatedImage.documentId,
        updatedImageDocument: { ...updatedImage, isProcessing: true },
      })
    );

    // Debounced backend call - cancel previous timer and set new one
    const debounceKey = `${documentId}_${onePage ? pageNumber : 'all'}`;

    // Clear any existing timer for this document/page combination
    const existingTimer = rotateDebounceTimers.get(debounceKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new debounced timer
    const timer = setTimeout(async () => {
      if (onePage) {
        // Single page rotation
        const backendData = {
          documentId: id,
          rotationAngle: convertAngle(angle),
          isCroppedVersion: Boolean(isCropped),
          pageNumber: isPDF ? pageNumber : 1,
        };

        await dispatch(
          backendCall(
            createApiCall((api) => api.imageHandling.rotate, {
              params: backendData,
              onError: () => {
                console.error(`Failed to rotate image with documentId ${documentId}, page ${pageNumber}`);
              },
            })
          )
        );
        trackEvent(TRACKING_EVENTS.DOCUMENT, {
          action: 'rotated',
          rotationDegrees: convertAngle(angle),
        });
      } else {
        // Multiple pages rotation - create array of promises
        const rotationPromises = Array.from({ length: pageCount }, (_, i) => {
          const currentPageNumber = i + 1;
          let currentPageAngle = getAngleAfterRotateForPage({ customRotate, pageNumber: currentPageNumber, cropSettings });

          const backendData = {
            documentId: id,
            rotationAngle: convertAngle(currentPageAngle),
            isCroppedVersion: Boolean(isCropped),
            pageNumber: isPDF ? currentPageNumber : 1,
          };

          return dispatch(
            backendCall(
              createApiCall((api) => api.imageHandling.rotate, {
                params: backendData,
                onError: () => {
                  console.error(`Failed to rotate image with documentId ${documentId}, page ${currentPageNumber}`);
                },
              })
            )
          );
        });

        // Wait for all rotation API calls to complete
        await Promise.all(rotationPromises);
        trackEvent(TRACKING_EVENTS.DOCUMENT, {
          action: 'rotated',
          rotationDegrees: convertAngle(angle),
        });
      }

      // Clean up timer from map after execution
      rotateDebounceTimers.delete(debounceKey);
    }, 2000);

    // Store the timer in our debounce map
    rotateDebounceTimers.set(debounceKey, timer);
  }
);

// HELPERS
function convertAngle(a: number) {
  if (a >= 360) {
    return convertAngle(a - 360);
  }
  return a;
}

function getAngleAfterRotateForPage({
  customRotate,
  pageNumber,
  cropSettings,
}: {
  customRotate: { [key: number]: number };
  pageNumber: number;
  cropSettings: ODataDocumentCropSettings[] | RetrievedDocumentReferenceCropSettings[];
}): number {
  const crop = cropSettings?.find((cs) => cs?.pageNumber === pageNumber);
  return ((crop ? crop.rotationAngle + customRotate[pageNumber] : customRotate[pageNumber]) || 0) + 90;
}
